import {
    AdEvent,
    AdState,
    ArticlePublication,
    DataLayerEventName,
    EventPublication,
    LiveBlogEvents,
    SharingEvent,
    wait,
} from '@news-mono/web-common'
import { WebsocketServiceMessageWithPayload } from '@west-australian-newspapers/websocket-message-types'
import {
    default as React,
    FC,
    Fragment,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Logger } from 'typescript-log'
import { useWebsocketServiceClient } from '../../websocket-service-client/use-websocket-service'
import {
    JumpToTopButton,
    LiveEventLoadButton,
    LiveEventTimeline,
} from './Components'
import { LiveEventMilestones } from './Components/LiveEventMilestones'
import {
    getPostIdFromUrl,
    isDebuggingEvents,
    isPublishedMilestone,
    isPublishedPinnedPost,
    liveEventDebug,
    NewLiveEventItem,
    scrollToElement,
    spliceNewPostsIntoArray,
    useDebugPanel,
    useFetchEventPosts,
    useIsComponentOnScreen,
    usePostManager,
} from './helpers'
import {
    StyledButtonContainer,
    StyledContainer,
    StyledJumpButtonContainer,
    StyledReportingLiveBubble,
    StyledReportingLiveContainer,
    StyledReportingLiveDivider,
    StyledReportingLiveText,
} from './LiveEvent.styled'

export interface LiveEventProps {
    publication: EventPublication | ArticlePublication
    log: Logger
    adState: AdState
    onEvent: (event: LiveBlogEvents | SharingEvent | AdEvent) => void
    onWebsocketEvent: (
        event: WebsocketServiceMessageWithPayload<NewLiveEventItem>,
    ) => void
}

export const LiveEvent: FC<LiveEventProps> = ({
    publication,
    log,
    adState,
    onEvent,
    onWebsocketEvent,
}) => {
    const { fetchEventPosts, isFetching } = useFetchEventPosts()

    useWebsocketServiceClient<NewLiveEventItem>({
        entityId: publication.id,
        onSubscribe: (messages) =>
            messages.map(({ payload }) => payload).map((post) => addPost(post)),
        onMessage: (message) => {
            if (message.payload) {
                addDebugEvent('Websocket message received %o', message.payload)
                addPost(message.payload)

                // Check for a milestone and add to item array
                if (isPublishedMilestone(message.payload)) {
                    setMilestones(
                        spliceNewPostsIntoArray([message.payload], milestones),
                    )
                }

                // Check for a pinned/sticky and add to item array
                if (isPublishedPinnedPost(message.payload)) {
                    setPinnedPosts(
                        spliceNewPostsIntoArray([message.payload], pinnedPosts),
                    )
                }
            }
            // Update article updated timestamp in header
            onWebsocketEvent(message)
        },
    })

    const postsPerPage = 10

    const {
        initialPostState,
        activePosts,
        postBuffer,
        setActivePosts,
        processPostBuffer,
        addPosts,
        addPost,
        oldestVisiblePost,
        newestVisiblePost,
        morePosts,
    } = usePostManager(
        publication.kind === 'event' ? publication.entries.documents : [],
    )

    interface FetchPostsArgs {
        postId?: string
        before?: number
        after?: number
        callback?: (posts: NewLiveEventItem[]) => Promise<void>
        replaceActivePosts?: boolean
    }

    const fetchPosts = useCallback(
        async ({
            postId,
            before = 0,
            after = 0,
            callback,
            replaceActivePosts,
        }: FetchPostsArgs) => {
            const response = await fetchEventPosts({
                publicationId: publication.id,
                reference: postId,
                after,
                before,
            })

            // Minimise turbulent loading
            replaceActivePosts && setActivePosts([])

            await wait(5)

            addPosts(response.documents, postId ?? response.documents[0].id, {
                newerPosts: response.totalAfter,
                olderPosts: response.totalBefore,
            })

            callback && (await callback(response.documents))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [publication.id, addPosts],
    )

    /**
     * Initial state for article publications
     */

    // posts are prefetched for event publications
    const [initialPostsFetched, setInitialPostsFetched] = useState(
        publication.kind === 'event',
    )

    useEffect(() => {
        if (publication.kind === 'article') {
            addDebugEvent('Fetching initial posts for article publication')
            fetchPosts({
                before: 10,
                after: 0,
                callback: async (posts) => {
                    onWebsocketEvent({
                        kind: 'create-post',
                        entityId: publication.id,
                        payload: posts[0],
                    })
                    setInitialPostsFetched(true)
                    addDebugEvent(
                        'Initial posts fetched for article publication',
                    )
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [publication.kind])

    /**
     * Deep linking
     */

    const deepLinkPostId = getPostIdFromUrl()

    const isDeepLinkPostVisible = !!activePosts.find(
        ({ id }) => id === deepLinkPostId,
    )

    const [deepLinkPostsFetching, setDeepLinkPostsFetching] = useState(false)

    useEffect(() => {
        if (deepLinkPostsFetching) {
            return
        }

        if (!initialPostsFetched) {
            addDebugEvent(
                'Initial posts have not been fetched, skipping deep link check',
            )
            return
        }

        if (!deepLinkPostId) {
            addDebugEvent('No deep link post in hash')
            return
        }

        if (isDeepLinkPostVisible && !morePosts.newer) {
            addDebugEvent('Deep link post found in active posts, scrolling')
            scrollToElement(deepLinkPostId)
            return
        }

        addDebugEvent('Deep link post not found in active posts, fetching')

        scrollToElement(jumpContainer.current)

        setDeepLinkPostsFetching(true)
        fetchPosts({
            postId: deepLinkPostId,
            after: 0,
            before: 9,
            replaceActivePosts: true,
            callback: async () => {
                setDeepLinkPostsFetching(false)
                await wait(5)

                scrollToElement(deepLinkPostId)
            },
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deepLinkPostId, isDeepLinkPostVisible, initialPostsFetched])

    /**
     * Lazy Load
     */

    const [lazyLoadTriggered, setLazyLoadTriggered] = useState(false)

    const handleLazyLoad = async () => {
        if (!lazyLoadTriggered && !isFetching && oldestVisiblePost) {
            addDebugEvent('Lazy loading next posts')
            setLazyLoadTriggered(true)
            await fetchPosts({
                postId: oldestVisiblePost.id,
                before: postsPerPage,
            })
        }
    }

    /**
     * Load More
     */

    const handleLoadMore = async () => {
        addDebugEvent('Load more clicked')
        if (oldestVisiblePost) {
            await fetchPosts({
                postId: oldestVisiblePost.id,
                before: postsPerPage,
            })

            onEvent({
                type: DataLayerEventName.loadMoreOlderPosts,
                originator: `LiveEvent`,
                payload: {
                    publicationId: publication.id,
                    lastPostPrior: oldestVisiblePost.id,
                },
            })
        }
    }

    /**
     * Jump to top
     */

    const jumpContainer = useRef(null)

    const handleJumpToTop = async () => {
        liveEventDebug('Jumping to top')
        scrollToElement(jumpContainer.current)

        /**
         * If we have posts in the buffer (and we're not in a deep link state), add those to the item array
         */
        if (postBuffer.length > 0 && !isDeepLinkPostVisible) {
            processPostBuffer()
            return
        }

        /**
         * If we're in a deep link state load the latest posts and replace everything
         */
        if (morePosts.newer) {
            addDebugEvent('More posts detected, loading latest known post')

            /**
             * Get the ID of either the latest buffer post or the first known post from the initial load
             */
            const postId = postBuffer[0]?.id || initialPostState[0]?.id

            /**
             * Clear the deep link ID if found
             */
            window.location.hash = ''

            fetchPosts({
                postId,
                after: 5,
                before: 9,
                replaceActivePosts: true,
                callback: async () => {
                    await wait(5)
                    scrollToElement(jumpContainer.current)
                },
            })
        }
    }

    const isActiveEvent = publication.classification?.label === 'live'

    const { isVisible, componentRef: containerRef } = useIsComponentOnScreen()

    /**
     * Milestones
     */

    const [milestones, setMilestones] = useState(
        publication.kind === 'event' ? publication.entries.milestones : [],
    )

    const hasMilestones = milestones.length > 0

    /**
     * Pinned / Sticky Posts
     */

    const [pinnedPosts, setPinnedPosts] = useState(
        publication.kind === 'event' ? publication.entries.stickies : [],
    )

    /**
     * Debug
     */

    const debugObj = {
        activePosts: activePosts.length,
        postBuffer: postBuffer.length,
        morePosts,
        isFetching,
        isVisible,
        newestVisiblePost: newestVisiblePost?.id,
        oldestVisiblePost: oldestVisiblePost?.id,
        deepLinkPostId,
        isDeepLinkPostVisible,
        lazyLoadTriggered,
    }

    const { DebugPanel, addDebugEvent } = useDebugPanel({ debugObj })

    /**
     * Count the posts above the current timeline, either by webhook posts in the buffer or tracked count
     */
    const newPostCount =
        postBuffer.length !== 0
            ? postBuffer.length
            : morePosts.newer !== 0
            ? morePosts.newer
            : 0

    return (
        <StyledContainer ref={containerRef}>
            <StyledJumpButtonContainer ref={jumpContainer}>
                <JumpToTopButton
                    onClick={handleJumpToTop}
                    inViewport={isVisible}
                    isLoading={isFetching}
                    newPostCount={newPostCount}
                />
            </StyledJumpButtonContainer>
            {hasMilestones && (
                <LiveEventMilestones
                    milestones={milestones}
                    onEvent={onEvent}
                />
            )}
            <StyledReportingLiveContainer isActive={isActiveEvent}>
                {isActiveEvent && (
                    <Fragment>
                        <StyledReportingLiveBubble />
                        <StyledReportingLiveText>
                            Reporting
                            <span> LIVE</span>
                        </StyledReportingLiveText>
                    </Fragment>
                )}
            </StyledReportingLiveContainer>
            {isActiveEvent && <StyledReportingLiveDivider />}

            <LiveEventTimeline
                posts={activePosts}
                pinnedPosts={pinnedPosts}
                eventTitle={publication.heading}
                log={log}
                triggerLazyLoad={handleLazyLoad}
                onEvent={onEvent}
                adState={adState}
            />
            <StyledButtonContainer>
                {morePosts.older > 0 && (
                    <LiveEventLoadButton
                        isLoading={isFetching}
                        onClick={handleLoadMore}
                    />
                )}
            </StyledButtonContainer>
            {isDebuggingEvents && <DebugPanel />}
        </StyledContainer>
    )
}
